import React from 'react';
import { Lock, RotateCcw, Users, Crown, Shield, Globe, Smartphone, Infinity, Headphones, DollarSign, Calendar, History, Zap, BarChart3, Settings, CheckCircle } from 'lucide-react';
import { UserCountDisplay } from './icons/UsersIcon';

const OptimizedChatGPTPlans = ({ onSelectPlan }) => {
  const plans = [
    {
      id: 'official',
      title: 'Official ChatGPT Plus Account',
      description: 'Your personal ChatGPT Plus account with exclusive access and full official platform integration.',
      price: '$15.99',
      period: '/month',
      users: 1,
      image: '/images/gpt-main.jpg',
      features: [
        { icon: <Lock />, text: 'Exclusive Access: Personal ChatGPT Plus account' },
        { icon: <Globe />, text: 'Official Platform: Direct access via ChatGPT.com' },
        { icon: <Smartphone />, text: 'Full Compatibility: Web, mobile, desktop apps' },
        { icon: <Shield />, text: 'Maximum Privacy: Complete data isolation' },
        { icon: <Infinity />, text: 'Unlimited Usage: No restrictions or sharing' },
        { icon: <Headphones />, text: 'Official Support: Direct OpenAI support' }
      ],
      buttonText: 'Purchase Official Account',
      highlights: ['Most Popular', 'Best Privacy', 'Official Support']
    },
    {
      id: 'renewal',
      title: 'Account Renewal Service',
      description: 'Seamless renewal for your existing ChatGPT Plus account at discounted rates.',
      price: '$12.99',
      period: '/month',
      originalPrice: '$20.00',
      users: 1,
      image: '/images/gpt-main.jpg',
      features: [
        { icon: <RotateCcw />, text: 'Automatic Renewal: Hassle-free account extension' },
        { icon: <DollarSign />, text: 'Cost Savings: Up to 40% off official renewal rates' },
        { icon: <Calendar />, text: 'Flexible Terms: 1, 3, 6, or 12-month options' },
        { icon: <History />, text: 'Account Continuity: Keep your chat history & settings' },
        { icon: <Zap />, text: 'Instant Processing: Renewal within 30 minutes' },
        { icon: <Headphones />, text: 'Dedicated Support: Priority assistance for renewals' }
      ],
      buttonText: 'Renew My Account',
      highlights: ['Save 35%', 'Existing Users', 'Fast Renewal']
    },
    {
      id: '3person',
      title: '3-Person Shared Access',
      description: 'Perfect for small teams or families - share ChatGPT Plus with 2 others at massive savings.',
      price: '$24.99',
      period: '/month',
      pricePerUser: '($8.33/user)',
      originalPrice: '$47.97',
      users: 3,
      image: '/images/gpt-main.jpg',
      features: [
        { icon: <Users />, text: '3 User Slots: Share with friends, family, or colleagues' },
        { icon: <DollarSign />, text: 'Split Costs: Only $8.33/month per person' },
        { icon: <Lock />, text: 'Individual Privacy: Separate conversation isolation' },
        { icon: <Zap />, text: 'Full Features: GPT-4o, o3, plugins, custom GPTs' },
        { icon: <RotateCcw />, text: 'Fair Usage: Optimized queue management' },
        { icon: <Settings />, text: 'Group Management: Easy user addition/removal' }
      ],
      buttonText: 'Get 3-Person Access',
      highlights: ['Team Solution', '3 Users', 'Great Value']
    },
    {
      id: '6person',
      title: '6-Person Shared Access',
      description: 'Maximum value for larger teams - enterprise-grade features at consumer prices.',
      price: '$24.99',
      period: '/month',
      pricePerUser: '($4.17/user)',
      originalPrice: '$95.94',
      badge: 'BEST VALUE',
      users: 6,
      image: '/images/gpt-main.jpg',
      features: [
        { icon: <Users />, text: '6 User Slots: Ideal for teams and departments' },
        { icon: <Crown />, text: 'Best Value: Only $4.17/month per person' },
        { icon: <Shield />, text: 'Enhanced Security: Advanced user isolation' },
        { icon: <Zap />, text: 'Priority Access: Reduced wait times' },
        { icon: <BarChart3 />, text: 'Usage Analytics: Team performance insights' },
        { icon: <Settings />, text: 'Admin Dashboard: Centralized user management' }
      ],
      buttonText: 'Get 6-Person Access',
      featured: true,
      highlights: ['Maximum Value', '6 Users', 'Enterprise Features']
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-black text-white rounded-full px-6 py-3 text-sm font-semibold mb-6">
            <Crown className="w-4 h-4 mr-2" />
            Premium Plans
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choose Your ChatGPT Plus Access Plan
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Select the perfect plan that fits your needs and budget
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => {
            const savings = plan.originalPrice ? (parseFloat(plan.originalPrice.replace('$', '')) - parseFloat(plan.price.replace('$', ''))).toFixed(2) : null;
            
            return (
              <div
                key={plan.id}
                className={`group relative bg-white rounded-3xl shadow-lg border-2 p-8 cursor-pointer 
                  hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 ease-out overflow-hidden ${
                  plan.featured 
                    ? 'border-black shadow-xl' 
                    : 'border-gray-200 hover:border-gray-400'
                }`}
                onClick={() => onSelectPlan(plan.id)}
              >
                {/* 微妙装饰背景 */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-gray-100 rounded-full opacity-20 blur-3xl"></div>
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gray-100 rounded-full opacity-20 blur-3xl"></div>

                {plan.badge && (
                  <div className="absolute -top-3 right-8 bg-black text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
                    {plan.badge}
                  </div>
                )}
                
                <div className="relative z-10">
                  {/* 计划头部 - 图片和用户数量同一行 */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-xl group-hover:scale-110 transition-transform duration-300 bg-gray-100">
                      <img 
                        src={plan.image} 
                        alt={plan.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // 如果图片加载失败，显示默认图标
                          e.target.style.display = 'none';
                          e.target.parentElement.className = "w-16 h-16 bg-gradient-to-br from-gray-800 to-black rounded-2xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300";
                          e.target.parentElement.innerHTML = '<svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>';
                        }}
                      />
                    </div>
                    
                    {/* 用户数量显示 - 右侧 */}
                    <div className="flex flex-col items-center">
                      <UserCountDisplay count={plan.users} size="sm" />
                    </div>
                  </div>
                  
                  {/* 产品标题 - 单独一行 */}
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-black transition-colors">
                      {plan.title}
                    </h3>
                  </div>
                  
                  {/* 价格区域 - 营销导向设计 */}
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100 shadow-inner mb-8">
                    <div className="flex items-baseline justify-between mb-4">
                      <div className="flex items-baseline gap-3">
                        <span className="text-4xl font-bold text-black">{plan.price}</span>
                        <span className="text-gray-600 text-lg">{plan.period}</span>
                        {plan.originalPrice && (
                          <span className="text-lg text-gray-400 line-through">{plan.originalPrice}</span>
                        )}
                      </div>
                      {savings && (
                        <div className="bg-black text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
                          省 ${savings}
                        </div>
                      )}
                    </div>
                    
                  </div>
                  
                  {/* 功能列表 - 优化版本 */}
                  <div className="space-y-4 mb-8">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-black flex-shrink-0">
                            {feature.icon}
                          </span>
                          <span className="text-gray-700 leading-relaxed">{feature.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 亮点标签 */}
                  <div className="flex flex-wrap gap-2 mb-8">
                    {plan.highlights.map((highlight, idx) => (
                      <span 
                        key={idx} 
                        className="inline-flex items-center bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full text-sm font-medium border border-gray-200"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>
                  
                  {/* CTA按钮 - 专业设计 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectPlan(plan.id);
                    }}
                    className={`w-full py-4 rounded-2xl font-bold text-lg transition-all duration-300 shadow-lg ${
                      plan.featured 
                        ? 'bg-black text-white hover:bg-gray-800 hover:shadow-xl' 
                        : 'border-2 border-black text-black hover:bg-black hover:text-white hover:shadow-xl'
                    }`}
                  >
                    {plan.buttonText}
                  </button>
                </div>

                {/* 悬停边框效果 */}
                <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="absolute inset-0 rounded-3xl border-2 border-black/10"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 比较表格 - 简化设计 */}
        <div className="mt-16 overflow-x-auto">
          <div className="max-w-5xl mx-auto bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left font-bold text-gray-900">Feature</th>
                  <th className="px-6 py-4 text-center font-bold text-gray-900">Official</th>
                  <th className="px-6 py-4 text-center font-bold text-gray-900">Renewal</th>
                  <th className="px-6 py-4 text-center font-bold text-gray-900">3-Person</th>
                  <th className="px-6 py-4 text-center font-bold text-gray-900">6-Person</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-semibold text-gray-900">Monthly Cost</td>
                  <td className="px-6 py-4 text-center text-gray-700">$15.99</td>
                  <td className="px-6 py-4 text-center text-gray-700">$12.99</td>
                  <td className="px-6 py-4 text-center text-gray-700">$8.33/user</td>
                  <td className="px-6 py-4 text-center font-bold text-black">$4.17/user</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-semibold text-gray-900">Users</td>
                  <td className="px-6 py-4 text-center text-gray-700">1</td>
                  <td className="px-6 py-4 text-center text-gray-700">1</td>
                  <td className="px-6 py-4 text-center text-gray-700">3</td>
                  <td className="px-6 py-4 text-center text-gray-700">6</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-semibold text-gray-900">GPT-4o Access</td>
                  <td className="px-6 py-4 text-center text-black text-xl">✓</td>
                  <td className="px-6 py-4 text-center text-black text-xl">✓</td>
                  <td className="px-6 py-4 text-center text-black text-xl">✓</td>
                  <td className="px-6 py-4 text-center text-black text-xl">✓</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-semibold text-gray-900">Custom GPTs</td>
                  <td className="px-6 py-4 text-center text-black text-xl">✓</td>
                  <td className="px-6 py-4 text-center text-black text-xl">✓</td>
                  <td className="px-6 py-4 text-center text-black text-xl">✓</td>
                  <td className="px-6 py-4 text-center text-black text-xl">✓</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-semibold text-gray-900">Usage Analytics</td>
                  <td className="px-6 py-4 text-center text-gray-400 text-xl">—</td>
                  <td className="px-6 py-4 text-center text-gray-400 text-xl">—</td>
                  <td className="px-6 py-4 text-center text-gray-400 text-xl">—</td>
                  <td className="px-6 py-4 text-center text-black text-xl">✓</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OptimizedChatGPTPlans;